'use client';

import { Grid, MediaCard, Text, InlineStack, Badge } from '@shopify/polaris';
import FacebookInstagramIcon from '@/public/assets/media-provider-icons/facebookInstagram.svg';
import TikTokIcon from '@/public/assets/media-provider-icons/tiktok.svg';
import TolstoyIcon from '@/public/assets/media-provider-icons/tolstoy.svg';
import { IntegrationService } from '@/features/integrations';
import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import useProviderConnectionStatus from '@/hooks/useProviderConnectionStatus';
import { useDeleteApp } from '@/api/mutations/useDeleteApp';
import StudioRoutes from '@/utils/routes';
import useRouterNavigation from '@/hooks/useRouterNavigation';
import { useCurrentApp } from '@/stores/userDataStore';

const Providers = () => {
  const { pushPath } = useRouterNavigation();
  const { owner: userId, appKey } = useCurrentApp();
  const { connectionStatusByProvider, appsByProvider, isLoading } = useProviderConnectionStatus();

  const deleteAppMutation = useDeleteApp();

  const providers = [
    {
      key: MediaAssetProviders.instagram,
      title: 'Facebook/Instagram',
      illustration: FacebookInstagramIcon.src,
      connectAction: () => IntegrationService.handleInstagramConnect({ appKey }),
      disconnectAction: () => {
        const app = appsByProvider[MediaAssetProviders.instagram]?.[0];
        deleteAppMutation(app.id);
      },
      description: 'Connect your Facebook/Instagram account to import photos and videos.',
    },
    {
      key: MediaAssetProviders.tiktok,
      title: 'TikTok',
      illustration: TikTokIcon.src,
      connectAction: () => IntegrationService.handleTiktokConnect({ userId, appKey }),
      disconnectAction: () => {
        const app = appsByProvider[MediaAssetProviders.tiktok]?.[0];
        deleteAppMutation(app.id);
      },
      description: 'Import your TikTok videos and use them in your projects.',
    },
    {
      key: MediaAssetProviders.playApp,
      title: 'Tolstoy',
      illustration: TolstoyIcon.src,
      connectAction: () => IntegrationService.handleTolstoyConnect(),
      disconnectAction: () => {
        const app = appsByProvider[MediaAssetProviders.playApp]?.[0];
        deleteAppMutation(app.id);
      },
      description: 'Access your Tolstoy library for seamless integration.',
    },
  ];

  return (
    <Grid columns={{ xs: 1, sm: 2, md: 2, lg: 2, xl: 3 }}>
      {providers.map((provider) => {
        const isConnected = connectionStatusByProvider[provider.key];

        const getPrimaryAction = () => {
          if (isConnected) {
            return {
              content: 'View assets',
              onAction: () => pushPath(StudioRoutes.assets({ providerKey: provider.key })),
              loading: isLoading,
            };
          }

          return {
            content: `Connect ${provider.title}`,
            onAction: provider.connectAction,
            loading: isLoading,
          };
        };

        const getSecondaryAction = () => {
          if (isConnected) {
            return {
              content: 'Disconnect',
              onAction: provider.disconnectAction,
              loading: isLoading,
            };
          }
        };

        return (
          <Grid.Cell key={provider.key}>
            <MediaCard
              title={
                <InlineStack gap="200">
                  <Text variant="headingMd" as="h2">
                    {provider.title}
                  </Text>
                  {isConnected && <Badge tone="success">Connected</Badge>}
                </InlineStack>
              }
              description={provider.description}
              primaryAction={getPrimaryAction()}
              secondaryAction={getSecondaryAction()}
            >
              <img
                width="100px"
                style={{
                  padding: '15px',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  boxSizing: 'content-box',
                }}
                src={provider.illustration}
                alt={provider.title}
              />
            </MediaCard>
          </Grid.Cell>
        );
      })}
    </Grid>
  );
};

export default Providers;
